import React, { useEffect, useState } from 'react';
import {
  Card, Typography, Form, InputNumber, Input, Select, DatePicker, Row, Col, Divider, Button, message, Alert,
} from 'antd';
import { PlusOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchDeliveryEmployees, generateFlyInvoicePdf, fetchSmelters, fetchNextInvoiceNo } from '../api';
import { rupeesInWords } from '../../../utils/amountInWords';
import { HSN_BY_METAL, METAL_OPTIONS, hsnForMetal, goodsDescriptionForMetal } from '../constants';

const { Title, Text } = Typography;

const fmt = (n) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(typeof n === 'number' && !isNaN(n) ? n : 0);

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const round2Wt = (n) => Math.round((Number(n) || 0) * 10000) / 10000;

const GenerateFlyInvoice = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [smelters, setSmelters] = useState([]);

  useEffect(() => {
    fetchDeliveryEmployees()
      .then((res) => {
        if (res.success) setEmployees(res.data);
      })
      .catch((err) => console.error('Failed to load employees', err));

    fetchSmelters({ active: true })
      .then((res) => {
        if (res.success) setSmelters(res.data);
      })
      .catch((err) => console.error('Failed to load smelters', err));

    // Initialize form with defaults
    form.setFieldsValue({
      invoiceNo: '',
      invoiceDate: dayjs(),
      gstType: 'INTRA',
      metalType: 'GOLD',
      lineItems: [
        { description: goodsDescriptionForMetal('GOLD'), hsn: hsnForMetal('GOLD'), grossWeight: 0, meltingLoss: 0, netWeight: 0, rate: 0, amount: 0 },
      ],
      buyer: { name: '', gstin: '', pan: '', address: '', state: '' },
      cgstPercent: 1.5,
      sgstPercent: 1.5,
      igstPercent: 3,
      placeOfSupply: '',
      reverseCharge: false,
      total: 0,
      taxableValue: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
      roundOff: 0,
    });

    // Pre-fill the editable invoice number with the next AGS series number.
    fetchNextInvoiceNo()
      .then((res) => {
        if (res.success && res.data?.invoiceNo) form.setFieldsValue({ invoiceNo: res.data.invoiceNo });
      })
      .catch(() => {/* leave blank — user can type */});
  }, [form]);

  // CGST and SGST are the same percentage, so the bill has to print the SAME figure
  // twice — never 331.83 against 331.82. Each head is charged on the taxable value and
  // both take that one figure. (Mirror of the backend's utils/gstUtils.splitGst.)
  const splitGst = (taxableValue, ratePercent, isIntra) => {
    const taxable = round2(taxableValue);
    const rate = Number(ratePercent) || 0;
    if (!isIntra) {
      const igst = round2((taxable * rate) / 100);
      return { cgst: 0, sgst: 0, igst, gst: igst };
    }
    const half = round2((taxable * (rate / 2)) / 100);
    return { cgst: half, sgst: half, igst: 0, gst: round2(half * 2) };
  };

  // The GST inside an inclusive amount is split first (equal heads) and the taxable
  // value takes the remainder, so taxable + GST is exactly the inclusive amount.
  const backOutGst = (totalInclGst, ratePercent, isIntra) => {
    const total = round2(totalInclGst);
    const base = round2(total / (1 + (Number(ratePercent) || 0) / 100));
    const split = splitGst(base, ratePercent, isIntra);
    return { total, taxable: round2(total - split.gst), ...split };
  };

  // The smelter pays one GST-INCLUSIVE total. We back out the taxable value + GST
  // sitting inside it: taxable = total / (1 + rate); gst = total − taxable.
  const recomputeInclusive = () => {
    const vals = form.getFieldsValue();
    const total = Number(vals.total) || 0;
    const isIntra = vals.gstType === 'INTRA';
    const rate = isIntra
      ? (Number(vals.cgstPercent) || 0) + (Number(vals.sgstPercent) || 0)
      : (Number(vals.igstPercent) || 0);

    // Round Off adjusts the TAXABLE value and GST is charged on the adjusted figure,
    // so the round-off moves the GST and the grand total with it. With no round-off
    // the taxable is backed out of the entered total so the bill ties to it exactly.
    const roundOff = Number(vals.roundOff) || 0;
    const incl = backOutGst(total, rate, isIntra);
    const taxable = round2(incl.taxable + roundOff);
    const split = roundOff === 0 ? incl : splitGst(taxable, rate, isIntra);
    const cgstAmt = split.cgst, sgstAmt = split.sgst, igstAmt = split.igst;

    form.setFieldsValue({
      taxableValue: taxable,
      cgstAmount: isIntra ? cgstAmt : 0,
      sgstAmount: isIntra ? sgstAmt : 0,
      igstAmount: !isIntra ? igstAmt : 0,
    });
  };

  // The billed total: the round-off already sits inside the taxable value, so the
  // grand total is simply taxable + GST.
  const grandTotalOf = (get) => {
    const taxable = round2(get('taxableValue'));
    const gst = round2((Number(get('cgstAmount')) || 0) + (Number(get('sgstAmount')) || 0) + (Number(get('igstAmount')) || 0));
    return round2(taxable + gst);
  };

  // Switching metal retags the line items, but only those still carrying a known
  // default HSN/description — anything the user typed themselves is left alone.
  const onMetalChange = (metal) => {
    const knownHsn = Object.values(HSN_BY_METAL);
    const knownDesc = [goodsDescriptionForMetal('GOLD'), goodsDescriptionForMetal('SILVER')];
    const items = (form.getFieldValue('lineItems') || []).map((it) => ({
      ...it,
      hsn: (!it.hsn || knownHsn.includes(it.hsn)) ? hsnForMetal(metal) : it.hsn,
      description: (!it.description || knownDesc.includes(it.description))
        ? goodsDescriptionForMetal(metal) : it.description,
    }));
    form.setFieldsValue({ lineItems: items });
  };

  // Sum of GST-inclusive line amounts → default Total (still overridable).
  const syncTotalFromLines = () => {
    const items = form.getFieldValue('lineItems') || [];
    const total = round2(items.reduce((s, it) => s + (Number(it.amount) || 0), 0));
    form.setFieldsValue({ total });
    recomputeInclusive();
  };

  const onLineGrossChange = (idx) => {
    const items = form.getFieldValue('lineItems') || [];
    const it = items[idx] || {};
    const gross = Number(it.grossWeight) || 0;
    const loss = Number(it.meltingLoss) || 0;
    const net = round2Wt(Math.max(0, gross - loss));
    items[idx] = { ...it, netWeight: net, amount: round2(net * (Number(it.rate) || 0)) };
    form.setFieldsValue({ lineItems: items });
    syncTotalFromLines();
  };

  const onLineMeltLossChange = (idx) => {
    const items = form.getFieldValue('lineItems') || [];
    const it = items[idx] || {};
    const gross = Number(it.grossWeight) || 0;
    const loss = Number(it.meltingLoss) || 0;
    const net = round2Wt(Math.max(0, gross - loss));
    items[idx] = { ...it, netWeight: net, amount: round2(net * (Number(it.rate) || 0)) };
    form.setFieldsValue({ lineItems: items });
    syncTotalFromLines();
  };

  const onLineRateChange = (idx) => {
    const items = form.getFieldValue('lineItems') || [];
    const it = items[idx] || {};
    const net = Number(it.netWeight) || 0;
    items[idx] = { ...it, amount: round2(net * (Number(it.rate) || 0)) };
    form.setFieldsValue({ lineItems: items });
    syncTotalFromLines();
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const sm = smelters.find((s) => s._id === values.smelterId);
      const buyerData = sm ? {
        name: sm.name || '',
        gstin: sm.gstin || '',
        pan: sm.pan || '',
        address: sm.address || '',
        state: sm.state || '',
      } : { name: '' };

      const isIntra = values.gstType === 'INTRA';
      const rawTotal = round2(values.total);                 // pre-round-off anchor
      const taxable = round2(values.taxableValue);           // round-off already inside
      const total = grandTotalOf((k) => values[k]);          // grand total = taxable + GST
      // The entered line rate/amount are GST-inclusive; scale them down to the
      // pre-GST (taxable) values so the invoice's line amounts sum to the taxable
      // value and GST is shown separately (a compliant tax invoice).
      const factor = rawTotal > 0 ? taxable / rawTotal : 1;
      const lineItems = (values.lineItems || []).map((li) => {
        const net = Number(li.netWeight) || 0;
        const preAmount = round2((Number(li.amount) || 0) * factor);
        const preRate = net > 0 ? round2(preAmount / net) : round2((Number(li.rate) || 0) * factor);
        return {
          description: li.description,
          hsn: li.hsn,
          grossWeight: li.grossWeight,
          meltingLoss: li.meltingLoss,
          netWeight: li.netWeight,
          rate: preRate,
          amount: preAmount,
        };
      });

      const gstAmount = round2((Number(values.cgstAmount) || 0) + (Number(values.sgstAmount) || 0) + (Number(values.igstAmount) || 0));

      const payload = {
        invoiceNo: values.invoiceNo,
        metalType: values.metalType,
        invoiceDate: values.invoiceDate?.toISOString(),
        deliveredByUserId: values.deliveredByUserId,
        smelterId: values.smelterId,
        placeOfSupply: values.placeOfSupply,
        reverseCharge: values.reverseCharge,
        gstType: values.gstType,
        buyer: buyerData,
        lineItems,
        taxableValue: taxable,
        cgstPercent: isIntra ? (Number(values.cgstPercent) || 0) : 0,
        cgstAmount: isIntra ? round2(values.cgstAmount) : 0,
        sgstPercent: isIntra ? (Number(values.sgstPercent) || 0) : 0,
        sgstAmount: isIntra ? round2(values.sgstAmount) : 0,
        igstPercent: !isIntra ? (Number(values.igstPercent) || 0) : 0,
        igstAmount: !isIntra ? round2(values.igstAmount) : 0,
        gstAmount,
        roundOff: round2(values.roundOff),
        total,
      };
      await generateFlyInvoicePdf(payload, values.invoiceNo);
      message.success('Invoice generated and downloaded successfully.');
    } catch (error) {
      message.error(error?.response?.data?.message || 'Failed to generate invoice.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 12px' }}>
      <Title level={3}>Generate Tax Invoice</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Generate a tax invoice PDF on the fly. The smelter's amount is treated as GST-inclusive —
        the taxable value and GST are derived from it.
      </Text>

      <Card>
        <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
          {/* Header meta */}
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item label="Invoice No" name="invoiceNo" rules={[{ required: true }]}
                tooltip="Pre-filled with the next AGS number. Editable — the series advances only when a bill is issued.">
                <Input placeholder="AGS-2026-00130" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Invoice Date" name="invoiceDate" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Metal Delivered By" name="deliveredByUserId" rules={[{ required: true, message: 'Select employee' }]}>
                <Select showSearch placeholder="Select employee" optionFilterProp="label"
                  options={employees.map((e) => ({ value: e._id, label: e.employeeCode ? `${e.name} (${e.employeeCode})` : e.name }))} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item label="Place of Supply" name="placeOfSupply" rules={[{ required: true }]}>
                <Input placeholder="State or City" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="GST Type" name="gstType">
                <Select onChange={recomputeInclusive}
                  options={[
                    { value: 'INTRA', label: 'CGST + SGST' },
                    { value: 'INTER', label: 'IGST' },
                  ]} />
              </Form.Item>
            </Col>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.gstType !== curr.gstType}>
              {({ getFieldValue }) => {
                const isIntra = getFieldValue('gstType') === 'INTRA';
                return isIntra ? (
                  <>
                    <Col xs={12} md={4}>
                      <Form.Item label="CGST (%)" name="cgstPercent">
                        <InputNumber min={0} max={100} onChange={recomputeInclusive} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={4}>
                      <Form.Item label="SGST (%)" name="sgstPercent">
                        <InputNumber min={0} max={100} onChange={recomputeInclusive} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </>
                ) : (
                  <Col xs={24} md={8}>
                    <Form.Item label="IGST (%)" name="igstPercent">
                      <InputNumber min={0} max={100} onChange={recomputeInclusive} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                );
              }}
            </Form.Item>
            <Col xs={24} md={6}>
              <Form.Item label="Reverse Charge" name="reverseCharge">
                <Select options={[{ value: false, label: 'No' }, { value: true, label: 'Yes' }]} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: '12px 0', fontSize: 13 }}>Buyer Details</Divider>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item label="Buyer (Select Smelter)" name="smelterId" rules={[{ required: true, message: 'Please select a buyer' }]}>
                <Select
                  showSearch
                  placeholder="Select a smelter"
                  optionFilterProp="children"
                  onChange={(val) => {
                    const sm = smelters.find((s) => s._id === val);
                    if (sm) {
                      form.setFieldsValue({
                        placeOfSupply: sm.state || form.getFieldValue('placeOfSupply') || '',
                      });
                    }
                  }}
                  allowClear
                >
                  {smelters.map((s) => {
                    const displayName = (s.name || '').replace(/\s*\(\s*refinery\s*\)/gi, '').trim();
                    return <Select.Option key={s._id} value={s._id}>{displayName} - {s.gstin}</Select.Option>;
                  })}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: '12px 0', fontSize: 13 }}>Goods</Divider>
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item label="Metal" name="metalType"
                tooltip="Sets the default HSN and description for the line items below.">
                <Select options={METAL_OPTIONS} onChange={onMetalChange} />
              </Form.Item>
            </Col>
          </Row>
          <Alert
            type="info" showIcon style={{ marginBottom: 12 }}
            message="Rate & Amount below are the GST-inclusive figures the smelter pays. The invoice shows the pre-GST breakdown."
          />
          <Form.List name="lineItems">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <div key={field.key} style={{ background: '#fafafa', padding: '16px 16px 0', marginBottom: 12, borderRadius: 8, position: 'relative' }}>
                    {fields.length > 1 && (
                      <Button type="text" danger icon={<DeleteOutlined />} size="small" onClick={() => { remove(field.name); syncTotalFromLines(); }}
                        style={{ position: 'absolute', top: 4, right: 4 }} />
                    )}
                    <Row gutter={[16, 4]}>
                      <Col xs={24} md={9}>
                        <Form.Item label="Description of Goods" name={[field.name, 'description']} rules={[{ required: true, message: 'Required' }]}>
                          <Input placeholder="Old melted Gold" />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={5}>
                        <Form.Item label="HSN" name={[field.name, 'hsn']}>
                          <Input placeholder={hsnForMetal(form.getFieldValue('metalType'))} />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={5}>
                        <Form.Item label="Gross Wt (g)" name={[field.name, 'grossWeight']}>
                          <InputNumber min={0} precision={3} style={{ width: '100%' }} onChange={() => onLineGrossChange(field.name)} />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={5}>
                        <Form.Item label="Melting Loss (g)" name={[field.name, 'meltingLoss']} tooltip="Gross − Net. Editing this recalculates Net.">
                          <InputNumber min={0} precision={3} style={{ width: '100%' }} onChange={() => onLineMeltLossChange(field.name)} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 4]}>
                      <Col xs={12} md={8}>
                        <Form.Item label="Net Wt (g)" tooltip="Automatically computed (Gross − Loss). Only editable if you need to override.">
                          <Form.Item name={[field.name, 'netWeight']} noStyle>
                            <InputNumber min={0} precision={4} style={{ width: '100%' }} onChange={() => {
                              const items = form.getFieldValue('lineItems');
                              items[field.name].amount = round2((Number(items[field.name].netWeight) || 0) * (Number(items[field.name].rate) || 0));
                              form.setFieldsValue({ lineItems: items });
                              syncTotalFromLines();
                            }} />
                          </Form.Item>
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={8}>
                        <Form.Item label="Rate (₹/g, incl. GST)" name={[field.name, 'rate']} rules={[{ required: true }]}>
                          <InputNumber min={0} precision={2} style={{ width: '100%' }} onChange={() => onLineRateChange(field.name)} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item label="Amount (₹, incl. GST)">
                          <Form.Item name={[field.name, 'amount']} noStyle>
                            <InputNumber min={0} precision={2} style={{ width: '100%' }} onChange={syncTotalFromLines} />
                          </Form.Item>
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                ))}
                <Button type="dashed" onClick={() => {
                  const metal = form.getFieldValue('metalType');
                  add({ description: goodsDescriptionForMetal(metal), hsn: hsnForMetal(metal), grossWeight: 0, meltingLoss: 0, netWeight: 0, rate: 0, amount: 0 });
                }} block icon={<PlusOutlined />} style={{ marginBottom: 16 }}>
                  Add Line Item
                </Button>
              </>
            )}
          </Form.List>

          <Divider orientation="left" style={{ margin: '0 0 12px', fontSize: 13 }}>Tax & Summary</Divider>

          <Row justify="end">
            <Col xs={24} md={12}>
              <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8 }}>
                <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                  <Col><Text strong>Total from Smelter (incl. GST)</Text></Col>
                  <Col>
                    <Form.Item name="total" noStyle rules={[{ required: true }]}>
                      <InputNumber min={0} precision={2} onChange={recomputeInclusive} style={{ width: 150, textAlign: 'right' }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider style={{ margin: '8px 0' }} />

                <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                  <Col><Text type="secondary">Taxable Value</Text></Col>
                  <Col>
                    <Form.Item name="taxableValue" noStyle><InputNumber precision={2} readOnly style={{ width: 130, textAlign: 'right', border: 'none', background: 'transparent' }} /></Form.Item>
                  </Col>
                </Row>

                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.gstType !== curr.gstType}>
                  {({ getFieldValue }) => {
                    const isIntra = getFieldValue('gstType') === 'INTRA';
                    return isIntra ? (
                      <>
                        <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                          <Col><Text type="secondary">CGST Amount</Text></Col>
                          <Col><Form.Item name="cgstAmount" noStyle><InputNumber precision={2} readOnly style={{ width: 130, textAlign: 'right', border: 'none', background: 'transparent' }} /></Form.Item></Col>
                        </Row>
                        <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                          <Col><Text type="secondary">SGST Amount</Text></Col>
                          <Col><Form.Item name="sgstAmount" noStyle><InputNumber precision={2} readOnly style={{ width: 130, textAlign: 'right', border: 'none', background: 'transparent' }} /></Form.Item></Col>
                        </Row>
                      </>
                    ) : (
                      <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                        <Col><Text type="secondary">IGST Amount</Text></Col>
                        <Col><Form.Item name="igstAmount" noStyle><InputNumber precision={2} readOnly style={{ width: 130, textAlign: 'right', border: 'none', background: 'transparent' }} /></Form.Item></Col>
                      </Row>
                    );
                  }}
                </Form.Item>

                <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                  <Col>
                    <Text type="secondary">Round Off</Text>
                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                      (adjusts the taxable value — GST and the total follow)
                    </Text>
                  </Col>
                  <Col>
                    <Form.Item name="roundOff" noStyle>
                      <InputNumber precision={2} style={{ width: 130, textAlign: 'right' }} onChange={recomputeInclusive} />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider style={{ margin: '12px 0' }} />

                <Row justify="space-between" align="middle">
                  <Col><Text strong>Total Amount</Text></Col>
                  <Col>
                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldValue }) => (
                        <Text strong style={{ fontSize: 15, color: '#8C1D1D' }}>{fmt(grandTotalOf(getFieldValue))}</Text>
                      )}
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => {
                    const grand = grandTotalOf(getFieldValue);
                    return (
                      <div style={{ textAlign: 'right', marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{rupeesInWords(grand)}</Text>
                      </div>
                    );
                  }}
                </Form.Item>
              </div>
            </Col>
          </Row>

          <div style={{ marginTop: 24, textAlign: 'right' }}>
            <Button type="primary" htmlType="submit" icon={<DownloadOutlined />} loading={loading} size="large">
              Generate & Download PDF
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default GenerateFlyInvoice;
